import { PButton, PButtonPure } from '@porsche-design-system/components-react';
import { Column, RowData } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import {
  ActionGroup,
  MultiOrganizationSelect,
  MultiOrganizationSelectProps,
  Popover,
} from '../..';
import type { FilterTableFeatures } from '../table-features';

export const OrganizationFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
  TOrganization extends { id: number; name: string },
>({
  column,
  i18n,
  organizationSelect,
}: {
  column: Column<TFeatures, T, unknown>;
  i18n: {
    filterLabel: string;
    optionAll: string;
    buttonReset: string;
    buttonFilter: string;
  };
  organizationSelect: Omit<
    MultiOrganizationSelectProps<TOrganization>,
    'value' | 'onChange'
  >;
}) => {
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const filterValue = filterColumn.getFilterValue() as number[] | undefined;
  const [selection, setSelection] = useState(filterValue);
  const [isVisible, setVisibility] = useState(false);

  useEffect(() => {
    setSelection(filterValue);
  }, [filterValue]);

  return (
    <Popover
      target={
        <PButtonPure
          type="button"
          active={(filterValue?.length ?? 0) > 0}
          size="xs"
          icon="filter"
          onClick={() => setVisibility((value) => !value)}
          hideLabel
        >
          {i18n.filterLabel}
        </PButtonPure>
      }
      open={isVisible}
      onChange={setVisibility}
      placement="right-start"
      minWidth={800}
      maxWidth={800}
      withPortal
    >
      <MultiOrganizationSelect
        {...organizationSelect}
        value={selection ?? []}
        onChange={(value) => setSelection(value)}
        style={{ minWidth: '100%' }}
        variant="flat"
      />
      <ActionGroup spacing="md">
        <PButton
          type="button"
          variant="secondary"
          icon="close"
          onClick={() => {
            filterColumn.setFilterValue(undefined);
            setSelection(undefined);
            setVisibility(false);
          }}
        >
          {i18n.buttonReset}
        </PButton>

        <PButton
          type="button"
          onClick={() => {
            filterColumn.setFilterValue(
              // Set undefined to remove the filter entirely
              (selection?.length ?? 0) > 0 ? selection : undefined,
            );
            setVisibility(false);
          }}
        >
          {i18n.buttonFilter}
        </PButton>
      </ActionGroup>
    </Popover>
  );
};
